import type { LayerSpec, LayerTreeNode, LayerVisibilityPatch } from "../layers";
import type { MapType } from "../mapConfig";

/**
 * Control position type
 */
export type ControlPosition = "top-left" | "top-right" | "bottom-left" | "bottom-right";

/**
 * Base control configuration
 */
interface BaseControl {
  /**
   * Optional unique identifier for the control
   * Useful when rendering multiple controls of the same type
   * @default auto-generated from type and index
   */
  id?: string;
  /**
   * Position of the control on the map
   * @default varies by control type
   */
  position?: ControlPosition;
  /**
   * Custom inline styles for the control
   */
  style?: React.CSSProperties;
  /**
   * Additional CSS classes
   */
  className?: string;
}

/**
 * Navigation control configuration (compass + geolocate + reset zoom)
 */
interface NavigationControl extends BaseControl {
  type: "navigation";
  /**
   * Show compass button (mobile only)
   * @default true
   */
  showCompass?: boolean;
  /**
   * Show geolocate button
   * @default true
   */
  showGeolocate?: boolean;
  /**
   * Show reset zoom button at the top of the control group
   * Only appears when the view has changed from the initial position
   * @default true
   */
  showResetZoom?: boolean;
  /**
   * Callback when reset zoom is clicked
   */
  onResetZoom?: () => void;
  /**
   * Callback when compass is clicked
   */
  onCompassClick?: () => void;
  /**
   * Callback when geolocate is clicked
   */
  onGeolocateClick?: () => void;
}

/**
 * Layers control configuration (map type + layer toggles)
 */
interface LayersControl extends BaseControl {
  /** Hide redundant section titles and separate sections with subtle rules. */
  showSectionHeadings?: boolean;
  /** Controlled visibility and navigation for links into layer settings. */
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  stack?: string[];
  onStackChange?: (stack: string[]) => void;

  /** Content above map type and layer choices. */
  header?: React.ReactNode;
  mapTypePosition?: "top" | "bottom";
  type: "layers";
  /**
   * Current map type
   */
  currentMapType?: MapType;
  /**
   * Callback when map type is changed
   */
  onMapTypeChange?: (mapType: MapType) => void;
  /**
   * Layers with legend definitions that should appear in the control
   * Only layers with a `legend` property will be shown
   */
  layers?: LayerSpec[];
  /**
   * Callback when a layer's visibility is toggled
   */
  onLayerToggle?: (layerId: string) => void;
  /**
   * Callback to clear all layer selections (hide all layers)
   */
  onClearAll?: () => void;
  /**
   * Callback when a layer's selected variant changes. Only fires for layers
   * declaring `legend.variants`.
   */
  onLayerVariantChange?: (layerId: string, variantId: string) => void;
  /**
   * Recursive layer tree. When provided, the control renders the drill-down
   * layers panel in place of the flat list.
   */
  layerTree?: LayerTreeNode[];
  /**
   * Apply a visibility change from the panel, given as the target state per
   * affected layer. Container toggles and single-select choices both arrive as
   * one patch. Only used alongside `layerTree`.
   */
  onVisibilityChange?: (patch: LayerVisibilityPatch) => void;
}

/**
 * Search control configuration
 */
interface SearchControl extends BaseControl {
  type: "search";
  /**
   * Callback when search is triggered
   */
  onSearch?: (query: string) => void;
  /**
   * Placeholder text for search input
   * @default "Search places..."
   */
  placeholder?: string;
  /**
   * Show search input field
   * @default true
   */
  showSearchInput?: boolean;
}

/**
 * List toggle control configuration
 */
interface ListControl extends BaseControl {
  type: "list";
  /**
   * Callback when list toggle button is clicked
   */
  onToggle?: () => void;
  /**
   * Whether the list view is currently active (for visual feedback)
   * @default false
   */
  isActive?: boolean;
}

/**
 * Custom control configuration
 */
interface CustomControl extends BaseControl {
  type: "custom";
  /**
   * Custom render function for the control
   */
  render: (context: { mapRef: React.RefObject<any> }) => React.ReactNode;
}

/**
 * Map control discriminated union
 *
 * Use this type to configure which controls appear on the map.
 * Each control type has its own specific configuration options.
 *
 * @example
 * const controls: MapControl[] = [
 *   { type: 'navigation', position: 'bottom-right', showResetZoom: true },
 *   { type: 'search', position: 'top-left', onSearch: handleSearch },
 *   { type: 'list', position: 'top-right', onToggle: toggleList }
 * ];
 */
export type MapControl = NavigationControl | LayersControl | SearchControl | ListControl | CustomControl;

/**
 * Default control positions by type
 */
const DEFAULT_CONTROL_POSITIONS: Record<MapControl["type"], ControlPosition> = {
  navigation: "bottom-right",
  layers: "bottom-left",
  search: "top-left",
  list: "top-right",
  custom: "top-right",
};

/**
 * Get the effective position for a control, using default if not specified
 */
export function getControlPosition(control: MapControl): ControlPosition {
  return control.position ?? DEFAULT_CONTROL_POSITIONS[control.type];
}
